// Prévia de link — o que o cartão mostra (desenho da tela de 27/10/2026, §2.2).
//
// O `CartaoDeLink` não sabe de onde veio o que mostra: recebe uma `PreviaDoCartao` pronta. Aqui ela é montada de
// duas fontes: do link do próprio SE_WEB (nome da tela, sem rede) ou do cartão que o servidor leu do site. Funções
// puras, sem React: a forma (capa ou miniatura) e a limpeza do que o site mandou ficam num lugar só.

import type { CartaoDoSite } from "@/services/previasDeLinkApi";

import { dominioDe, ehDaCasa, type PrimeiroLink } from "./links";
import { nomeDaTela } from "./nomesDasTelas";

/** Como o link se veste no texto: branco sobre a bolha própria, azul-céu em todo o resto. */
export type TomDoLink = "propria" | "neutro";

/** Como o cartão se veste: a bolha própria (chat/atendimento), a do parceiro no portal, a dos outros e o feed. */
export type AparenciaDoCartao = "propria" | "propria-portal" | "dos-outros" | "feed";

/** `faixa` = capa 1,91:1 no topo; `compacta` = miniatura de 56 px (ou nenhuma imagem). */
export type FormaDoCartao = "faixa" | "compacta";

export interface PreviaDoCartao {
  /** Para onde o clique leva: o endereço do link. */
  url: string;
  /** O link é uma tela do próprio SE_WEB (`ehTelaDoLink`): o clique usa o router e o cartão leva a placa do logo. */
  casa: boolean;
  /** Só na casa: o caminho relativo (`/feed/posts/9?x=1#c`) do `<Link>`. */
  caminho?: string;
  titulo: string;
  /** `null` = sem descrição (o elemento nem existe). */
  descricao: string | null;
  imagem_url: string | null;
  imagem_largura: number | null;
  imagem_altura: number | null;
  /** O host do link, em minúsculas, com o `www.` (é "para onde o clique leva"). */
  dominio: string;
}

/** Largura mínima da capa, em px (a imagem de 299 px ou menos é ícone, não foto). */
const LARGURA_MINIMA_DA_CAPA = 300;

/** Sem caractere de controle (U+0000 a U+001F e U+007F): o navegador TIRA tab e LF de dentro de uma URL. */
function semControle(texto: string): string {
  let limpo = "";
  for (let i = 0; i < texto.length; i++) {
    const codigo = texto.charCodeAt(i);
    if (codigo > 0x1f && codigo !== 0x7f) limpo += texto[i];
  }
  return limpo;
}

function temControleOuBarraInvertida(texto: string): boolean {
  return semControle(texto) !== texto || texto.includes("\\");
}

/**
 * Um caminho que o router lê como DENTRO do app: começa com exatamente uma `/`, seguida de algo que não é `/`
 * nem `\`. `//evil.com/x` e `/\evil.com/x` (o navegador troca `\` por `/`) são lidos como outro host: o
 * `<Link>` do react-router trata `//` como URL absoluta e gera `<a href="//evil.com/x">`, sem `target` nem
 * `rel`, e o clique sai do site atrás do cartão com a marca da casa. Aqui os controles somem (tab e LF dentro de
 * `/<tab>/x` também viram `//x` no navegador) e as barras e os brancos do começo colapsam para uma `/` só.
 */
export function caminhoRelativoSeguro(caminho: string): string {
  return `/${semControle(caminho).replace(/^[\s/\\]+/, "")}`;
}

/**
 * O caminho relativo de um link da casa (`caminho` + `?consulta` + `#fragmento`), o que o `<Link>` do router
 * recebe, sempre dentro do app (`caminhoRelativoSeguro`: `https://<casa>//evil.com/x` leva a `/evil.com/x`, nunca
 * a outro host). Cai em `/` se o endereço não se lê.
 */
export function caminhoDaCasa(href: string): string {
  try {
    const url = new URL(href);
    return `${caminhoRelativoSeguro(url.pathname)}${url.search}${url.hash}`;
  } catch {
    return "/";
  }
}

/**
 * Extensões de arquivo estático. As dez primeiras são as do `location ~*` do nginx (`V1/nginx.docker.conf`, linha
 * 174): o nginx as serve do disco e nunca entregam o `index.html` do app. O resto são arquivos públicos que alguém
 * pode colar (o link colado pode ser de qualquer arquivo do domínio).
 */
const EXTENSAO_DE_ARQUIVO =
  /\.(?:js|css|png|jpe?g|gif|ico|svg|woff2?|ttf|eot|pdf|webp|mp4|webm|mp3|ogg|zip|json|txt|xml|map)$/i;

/**
 * O caminho relativo de um link da casa é uma TELA do app (o router o resolve) ou outra coisa que o mesmo host
 * serve (a API e o arquivo assinado do storage, `/health`, o service worker, um arquivo estático)? Só o *pathname*
 * conta (`?consulta` e `#fragmento` saem); `caminhoRelativoSeguro` primeiro, porque é o caminho que o router
 * leria (`//api/x` vira `/api/x`). Falso para `/api` e `/api/...`, `/health`, `/service-worker.js` e para o
 * caminho cujo ÚLTIMO segmento termina numa extensão de arquivo (`/drive/pasta.nova/` termina em `/`: é tela).
 * `/apis` e `/healthz` são telas. É a única regra do "link da casa que não abre uma tela": quem a usa trata o
 * link como link de fora (outra aba, sem router, sem cartão, sem consulta ao servidor).
 */
export function ehTelaDoApp(caminho: string): boolean {
  const pathname = caminhoRelativoSeguro(caminho.split(/[?#]/, 1)[0]);
  if (pathname === "/api" || pathname.startsWith("/api/")) return false;
  if (pathname === "/health" || pathname === "/service-worker.js") return false;
  const ultimoSegmento = pathname.slice(pathname.lastIndexOf("/") + 1);
  return !EXTENSAO_DE_ARQUIVO.test(ultimoSegmento);
}

/**
 * O endereço é do host do SE_WEB E o caminho dele é uma tela do app (`ehTelaDoApp`). É o que o `LinkNoTexto` e o
 * clique no corpo do post perguntam: só a tela da casa vai pelo router. O que é da casa mas não é tela (o arquivo
 * assinado `/api/v1/storage/file/...`, `/health`, `/logo.png`) se comporta como link de fora.
 */
export function ehTelaDaCasa(href: string, hostDaCasa?: string): boolean {
  return ehDaCasa(href, hostDaCasa) && ehTelaDoApp(caminhoDaCasa(href));
}

/** O mesmo para o `PrimeiroLink` do texto: `casa` (o host que a tela usou ao ler o texto) E o caminho é uma tela. */
export function ehTelaDoLink(link: PrimeiroLink): boolean {
  return link.casa && ehTelaDoApp(caminhoDaCasa(link.href));
}

/**
 * O endereço é `http:` ou `https:`, lido como o navegador lê (tab e LF dentro de `java<tab>script:` caem, e o
 * esquema continua sendo `javascript:`). É a guarda de quem monta um `<a href>`: qualquer outro esquema, ou um
 * endereço que não se lê, não vira link.
 */
export function ehHttpOuHttps(href: string): boolean {
  try {
    const { protocol } = new URL(href);
    return protocol === "http:" || protocol === "https:";
  } catch {
    return false;
  }
}

/** O cartão de um link do próprio SE_WEB: o nome da tela e o host, sem ir à rede. */
export function previaDaCasa(link: PrimeiroLink): PreviaDoCartao {
  const caminho = caminhoDaCasa(link.href);
  return {
    url: link.href,
    casa: true,
    caminho,
    titulo: nomeDaTela(caminho),
    descricao: null,
    imagem_url: null,
    imagem_largura: null,
    imagem_altura: null,
    dominio: dominioDe(link.href),
  };
}

/** Origem de mentira, só para conferir que um endereço relativo continua NELA depois de resolvido. */
const ORIGEM_DE_CONFERENCIA = "https://base.invalid";

/** Onde o servidor serve a imagem do cartão: o proxy assinado do storage (`_build_signed_proxy_url`, no backend). */
const PREFIXO_DA_IMAGEM = "/api/v1/storage/file/";

/**
 * A imagem do cartão só se carrega do NOSSO servidor: o caminho relativo da mesma origem que começa por
 * `/api/v1/storage/file/`, que é o único que o servidor monta (`imagem_url` vem do proxy assinado). `https://` de
 * qualquer host fica de fora: um endereço de fora faria o navegador de quem lê buscar a imagem lá (rastreador).
 * Se um dia a imagem vier de outro host (S3 assinado direto), abre-se o host exato aqui. Controle (tab e LF somem no
 * navegador: `/<tab>/evil.com` vira `//evil.com`) e `\` ficam de fora; e o caminho, resolvido, tem de continuar na
 * mesma origem E dentro do prefixo (`/api/v1/storage/file/../../x` começa pelo prefixo mas sai dele).
 */
function imagemAceita(imagemUrl: string | null): imagemUrl is string {
  if (!imagemUrl || temControleOuBarraInvertida(imagemUrl)) return false;
  if (!imagemUrl.startsWith(PREFIXO_DA_IMAGEM)) return false;
  try {
    const resolvida = new URL(imagemUrl, ORIGEM_DE_CONFERENCIA);
    return (
      resolvida.origin === ORIGEM_DE_CONFERENCIA && resolvida.pathname.startsWith(PREFIXO_DA_IMAGEM)
    );
  } catch {
    return false;
  }
}

/**
 * O cartão do site: o que o servidor leu, limpo para mostrar. `null` quando não há título (sem ele não há cartão).
 * O domínio é o host DO LINK (com o `www.`); o do servidor só vale se o link não se lê. Sem imagem aceita, os três
 * campos da imagem saem nulos juntos, para a forma não decidir por uma largura sem imagem.
 */
export function previaDoSite(link: PrimeiroLink, cartao: CartaoDoSite): PreviaDoCartao | null {
  const titulo = cartao.titulo.trim();
  if (titulo === "") return null;
  const descricao = cartao.descricao?.trim() ? cartao.descricao.trim() : null;
  const comImagem = imagemAceita(cartao.imagem_url);
  return {
    url: link.href,
    casa: false,
    titulo,
    descricao,
    imagem_url: comImagem ? cartao.imagem_url : null,
    imagem_largura: comImagem ? cartao.imagem_largura : null,
    imagem_altura: comImagem ? cartao.imagem_altura : null,
    dominio: dominioDe(link.href) || cartao.dominio,
  };
}

/**
 * Capa (`faixa`) só para imagem paisagem e grande: largura >= 300 E largura/altura >= 1,2 (a conta em inteiros,
 * `5 * largura >= 6 * altura`, para o 1,2 exato valer). Sem imagem, sem os dois números, da casa ou
 * `forcarCompacta` (o comentário do feed): `compacta`.
 */
export function formaDoCartao(previa: PreviaDoCartao, forcarCompacta = false): FormaDoCartao {
  if (forcarCompacta || previa.casa || !previa.imagem_url) return "compacta";
  const { imagem_largura: largura, imagem_altura: altura } = previa;
  if (largura === null || altura === null || largura <= 0 || altura <= 0) return "compacta";
  return largura >= LARGURA_MINIMA_DA_CAPA && 5 * largura >= 6 * altura ? "faixa" : "compacta";
}
